import { forwardRef } from "react";
import { useWeekStepper } from "@/hooks/useWeekStepper";
import { WeekStepperView } from "./WeekStepperView";

type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface WeekStepperProps {
  /** Fixed weekday (0 = Sunday, 1 = Monday, …) */
  weekday: Weekday;
  /** Current value (ISO string like "2025-01-29" or Date object) */
  value?: string | Date | null;
  /** Callback when date changes - receives ISO date string (YYYY-MM-DD) */
  onChange?: (value: string) => void;
  /** Callback when user confirms a selection (Enter, click, or blur) */
  onCommit?: () => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Minimum allowed date (ISO string or Date) */
  minDate?: string | Date | null;
  /** Maximum allowed date (ISO string or Date) */
  maxDate?: string | Date | null;
  /** Font size for scaling the widget (e.g., "0.75rem") */
  fontSize?: string;
  /** Additional CSS class */
  className?: string;
  /** Input id attribute */
  id?: string;
  /** Whether to auto-focus the input on mount */
  autoFocus?: boolean;
  /** Stable selector for end-to-end tests */
  testId?: string;
}

/**
 * Week-based date input component with prev/next buttons and a dropdown.
 * Steps by 7 days at a time, snapped to the specified weekday.
 */
export const WeekStepper = forwardRef<HTMLInputElement, WeekStepperProps>(
  function WeekStepper(
    {
      weekday,
      value,
      onChange,
      onCommit,
      disabled,
      minDate,
      maxDate,
      fontSize,
      className,
      id,
      autoFocus = false,
      testId,
    },
    ref
  ) {
    const api = useWeekStepper({
      weekday,
      value,
      onChange,
      disabled,
      minDate,
      maxDate,
    });

    return (
      <WeekStepperView
        {...api}
        className={className}
        size={fontSize}
        inputRef={ref}
        onCommit={onCommit}
        id={id}
        autoFocus={autoFocus}
        testId={testId}
      />
    );
  }
);

export interface MonDateInputProps extends Omit<WeekStepperProps, "weekday"> {}

/**
 * Convenience wrapper for Monday-only date inputs.
 * Equivalent to <WeekStepper weekday={1} ... />
 */
export const MonDateInput = forwardRef<HTMLInputElement, MonDateInputProps>(
  function MonDateInput(props, ref) {
    return <WeekStepper ref={ref} weekday={1} {...props} />;
  }
);
